import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import { editClass, getClasses, getClassesBySchoolId } from "../services/TeacherAPIClient";
import { getClassGradeLabel } from "../utils/classDisplay";
import "./TeacherRenameClassModal.css";

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase();

const getClassId = (classItem) => classItem?.id || classItem?._id;

const getAcademicYearFromClass = (classItem) =>
  classItem?.academicYear ||
  classItem?.academic_year ||
  classItem?.academicyear ||
  classItem?.academicYearLabel ||
  classItem?.yearLabel ||
  classItem?.schoolYear ||
  "";

const getStudentCountFromClass = (classItem) => {
  const possibleCount =
    classItem?.studentNumber ??
    classItem?.studentCount ??
    classItem?.studentsCount ??
    classItem?.pupilCount ??
    classItem?.students?.length;

  const numericCount = Number(possibleCount);
  return Number.isFinite(numericCount) ? numericCount : null;
};

const normaliseClass = (classItem, countryGrades) => {
  const grade = getClassGradeLabel(classItem, countryGrades);
  const academicYear = getAcademicYearFromClass(classItem);
  const studentCount = getStudentCountFromClass(classItem);
  const studentCopy =
    studentCount === null
      ? ""
      : `${studentCount} ${studentCount === 1 ? "child" : "children"}`;
  const detailLabel = [grade, academicYear, studentCopy].filter(Boolean).join(", ");

  return {
    ...classItem,
    id: getClassId(classItem),
    name: classItem?.name || classItem?.className || "Unnamed class",
    grade,
    academicYear,
    studentCount,
    detailLabel
  };
};

const normalisePrefill = (prefill) => ({
  className: normaliseText(prefill?.className || prefill?.class_name),
  newClassName: normaliseText(prefill?.newClassName || prefill?.new_class_name)
});

const findSingleExactClass = (classes, className) => {
  const target = normaliseComparableText(className);

  if (!target) {
    return null;
  }

  const matches = classes.filter(
    (classItem) => normaliseComparableText(classItem.name) === target
  );

  return matches.length === 1 ? matches[0] : null;
};

function TeacherRenameClassModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  prefill,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [classes, setClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);
  const [newClassName, setNewClassName] = useState("");
  const [renamedClass, setRenamedClass] = useState(null);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newClassNameTouched, setNewClassNameTouched] = useState(false);
  const [error, setError] = useState("");

  const trimmedSearchTerm = searchTerm.trim();
  const trimmedNewClassName = normaliseText(newClassName);
  const unchangedName =
    Boolean(selectedClass && trimmedNewClassName) &&
    normaliseComparableText(selectedClass.name) === normaliseComparableText(trimmedNewClassName);
  const duplicateClassName = useMemo(
    () =>
      Boolean(trimmedNewClassName) &&
      classes.some(
        (classItem) =>
          classItem.id !== selectedClass?.id &&
          normaliseComparableText(classItem.name) === normaliseComparableText(trimmedNewClassName)
      ),
    [classes, selectedClass, trimmedNewClassName]
  );
  const canContinue = Boolean(
    selectedClass && trimmedNewClassName && !unchangedName && !duplicateClassName && !isSubmitting
  );

  useEffect(() => {
    if (!show) {
      return;
    }

    const initialPrefill = normalisePrefill(prefill);

    setStage("form");
    setClasses([]);
    setSearchTerm(initialPrefill.className);
    setSelectedClass(null);
    setNewClassName(initialPrefill.newClassName);
    setRenamedClass(null);
    setIsSubmitting(false);
    setNewClassNameTouched(false);
    setError("");
    setIsLoadingClasses(true);

    Promise.all([
      getClasses(),
      schoolid ? getClassesBySchoolId(schoolid).catch(() => []) : Promise.resolve([])
    ])
      .then(([classesResponse, schoolClasses]) => {
        const studentCounts = new Map(
          (Array.isArray(schoolClasses) ? schoolClasses : []).map((classItem) => [
            getClassId(classItem),
            classItem.studentNumber
          ])
        );
        const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map((classItem) =>
            normaliseClass(
              studentCounts.has(getClassId(classItem))
                ? { ...classItem, studentNumber: studentCounts.get(getClassId(classItem)) }
                : classItem,
              countryGrades
            )
          )
          .filter((classItem) => classItem.id)
          .sort((classA, classB) => classA.name.localeCompare(classB.name));

        setClasses(normalisedClasses);
        setSelectedClass(findSingleExactClass(normalisedClasses, initialPrefill.className));
      })
      .catch((classesError) => {
        console.error("Error loading classes for rename:", classesError);
        setClasses([]);
        setError("Classes could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingClasses(false);
      });
  }, [show, prefill, countryGrades, schoolid]);

  const filteredClasses = useMemo(() => {
    const query = normaliseComparableText(trimmedSearchTerm);

    if (!query) {
      return classes;
    }

    return classes.filter((classItem) =>
      [classItem.name, classItem.grade, classItem.academicYear, classItem.detailLabel].some(
        (value) => normaliseComparableText(value).includes(query)
      )
    );
  }, [classes, trimmedSearchTerm]);

  const handleClassSelect = (classItem) => {
    setSelectedClass(classItem);
    setError("");
  };

  const handleSearchChange = (event) => {
    const nextSearchTerm = event.target.value;
    setSearchTerm(nextSearchTerm);
    setError("");

    if (
      selectedClass &&
      normaliseComparableText(nextSearchTerm) !== normaliseComparableText(selectedClass.name)
    ) {
      setSelectedClass(null);
    }
  };

  const handleSubmitRename = () => {
    setNewClassNameTouched(true);

    if (!canContinue) {
      return;
    }

    handleRenameClass();
  };

  const handleRenameClass = async () => {
    if (!canContinue) {
      setStage("form");
      return;
    }

    const completedRename = {
      ...selectedClass,
      oldName: selectedClass.name,
      name: trimmedNewClassName
    };

    setIsSubmitting(true);
    setError("");

    try {
      await editClass(selectedClass.id, trimmedNewClassName);

      setClasses((existingClasses) =>
        existingClasses.map((classItem) =>
          classItem.id === selectedClass.id
            ? { ...classItem, name: trimmedNewClassName }
            : classItem
        )
      );
      setRenamedClass(completedRename);
      setStage("success");
    } catch (renameError) {
      console.error("Error renaming class:", renameError);
      setError("The class could not be renamed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnotherRename = () => {
    setStage("form");
    setSearchTerm("");
    setSelectedClass(null);
    setNewClassName("");
    setRenamedClass(null);
    setIsSubmitting(false);
    setNewClassNameTouched(false);
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        message: "Class renamed."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderClassResults = () => {
    if (isLoadingClasses) {
      return (
        <div className="rename-class-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (filteredClasses.length < 1) {
      return (
        <p className="rename-class-muted">
          No matching classes found in your class list.
        </p>
      );
    }

    return filteredClasses.map((classItem) => {
      const isSelected = selectedClass?.id === classItem.id;

      return (
        <button
          key={classItem.id}
          type="button"
          className={`rename-class-result${isSelected ? " selected" : ""}`}
          onClick={() => handleClassSelect(classItem)}
          aria-label={`Select ${classItem.name}${classItem.detailLabel ? ` - ${classItem.detailLabel}` : ""}`}
          aria-pressed={isSelected}
        >
          <span className="rename-class-radio" aria-hidden="true" />
          <span className="rename-class-result-copy">
            <strong>{classItem.name}</strong>
            {classItem.detailLabel && <span>{classItem.detailLabel}</span>}
          </span>
          {isSelected && <span className="rename-class-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Rename Class</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="rename-class-intro">
          Choose the class and enter the new class name.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-3" controlId="renameClassSearch">
          <Form.Label>Which class are you renaming?</Form.Label>
          <Form.Text as="div" className="rename-class-field-label">
            Search class
          </Form.Text>
          <Form.Control
            type="search"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Start typing a class name"
            aria-label="Search class"
          />
        </Form.Group>

        <div className="rename-class-results" aria-label="Matching classes">
          {renderClassResults()}
        </div>

        <Form.Group className="mt-4" controlId="renameClassNewName">
          <Form.Label>New class name</Form.Label>
          <Form.Control
            type="text"
            value={newClassName}
            onBlur={() => setNewClassNameTouched(true)}
            onChange={(event) => {
              setNewClassName(event.target.value);
              setError("");
            }}
            placeholder="Enter the new class name"
            aria-label="New class name"
            isInvalid={
              newClassNameTouched &&
              (!trimmedNewClassName || unchangedName || duplicateClassName)
            }
          />
          {newClassNameTouched && !trimmedNewClassName && (
            <Form.Control.Feedback type="invalid">
              Enter a new class name.
            </Form.Control.Feedback>
          )}
          {newClassNameTouched && unchangedName && (
            <Form.Control.Feedback type="invalid">
              Enter a different class name.
            </Form.Control.Feedback>
          )}
          {newClassNameTouched && duplicateClassName && (
            <Form.Control.Feedback type="invalid">
              A class with this name already exists.
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="rename-class-footer-note">
          {selectedClass ? `${selectedClass.name} selected.` : "Choose a class first."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!canContinue} onClick={handleSubmitRename}>
          {isSubmitting ? "Renaming..." : "Rename class"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Class Renamed</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="success" className="rename-class-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>{renamedClass?.oldName} is now {renamedClass?.name}.</strong>
            <span>Children and results stay in the class.</span>
          </span>
        </Alert>
        <div className="rename-class-next">
          <strong>What happens next</strong>
          <span>{embedded ? "Done returns to SENSAI." : "Done returns to Help Centre home."}</span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={resetForAnotherRename}>
          Return to classes
        </Button>
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-rename-class-modal"
    >
      {stage === "form" && renderForm()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherRenameClassModal;
